import { useEffect, useState } from "react";
import type { HeroPerfReport } from "@/lib/hero-perf";
import {
  parseSrcSet,
  recordVerification,
  verifySrcSetChoice,
  type SrcSetVerification,
} from "@/lib/srcset-verify";

/**
 * Visible srcset candidate verifier for the QA hero frame.
 *
 * Waits for the `hero-perf` report (fired once the image finishes decoding),
 * resolves which candidate the browser picked, compares it to the smallest
 * candidate that covers `renderedWidth × dpr`, and checks the decode time
 * against the mobile/tablet/desktop target. Each result is persisted so
 * /qa/hero/perf can show the history.
 */
export const SrcsetVerifier = ({
  srcSets,
  decodeTargetMs,
}: {
  /** All candidate srcset strings offered to the browser (any format rung). */
  srcSets: (string | undefined)[];
  /** Optional override for the decode threshold (ms). */
  decodeTargetMs?: number;
}) => {
  const [result, setResult] = useState<SrcSetVerification | null>(null);

  useEffect(() => {
    const candidates = srcSets.flatMap((s) => parseSrcSet(s));

    const handle = (report: HeroPerfReport) => {
      const verification = verifySrcSetChoice(report, {
        candidates,
        decodeTargetMs,
      });
      setResult(verification);
      recordVerification(verification);
      // eslint-disable-next-line no-console
      console.info(
        `[srcset-verify] ${verification.pass ? "PASS" : "FAIL"} — ${verification.chosenFile} (${verification.chosenWidth ?? "?"}w) for a ${verification.requiredWidth ?? "?"}px slot, decode ${verification.decodeMs ?? "?"}ms / ${verification.decodeTargetMs}ms target`,
        verification,
      );
    };

    const onPerf = (event: Event) =>
      handle((event as CustomEvent<HeroPerfReport>).detail);
    window.addEventListener("hero-perf", onPerf as EventListener);
    if (window.__heroPerf) handle(window.__heroPerf);
    return () =>
      window.removeEventListener("hero-perf", onPerf as EventListener);
    // srcSets is a literal array from the caller; join for a stable dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [srcSets.join("|"), decodeTargetMs]);

  if (!result) {
    return (
      <div className="mt-3 rounded border border-border/60 bg-black/60 p-3 font-mono text-[11px] text-white/60">
        srcset verifier: waiting for decode…
      </div>
    );
  }

  const verdictTone =
    result.verdict === "optimal"
      ? "text-emerald-300"
      : result.verdict === "unknown"
        ? "text-amber-300"
        : "text-rose-300";

  return (
    <div
      data-testid="srcset-verifier"
      data-pass={result.pass ? "true" : "false"}
      className="mt-3 rounded border border-border/60 bg-black/70 p-3 font-mono text-[11px] leading-relaxed text-white/85"
    >
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span
          className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${
            result.pass ? "bg-emerald-500/80 text-black" : "bg-rose-500/85 text-white"
          }`}
        >
          {result.pass ? "PASS" : "FAIL"}
        </span>
        <span className="text-[10px] uppercase tracking-wider text-white/55">
          srcset candidate verifier · {result.breakpoint} ·{" "}
          {result.viewportWidth}px · dpr{result.dpr}
        </span>
      </div>

      <dl className="grid grid-cols-[auto,1fr] gap-x-3">
        <dt className="text-white/55">selected</dt>
        <dd className="break-all">
          {result.chosenFile}{" "}
          <span className="text-white/55">
            ({result.chosenWidth ?? "?"}w
            {result.transferKb != null ? ` · ${result.transferKb} KB` : ""})
          </span>
        </dd>

        <dt className="text-white/55">slot</dt>
        <dd>
          {result.renderedWidth ?? "?"} css px × {result.dpr} ={" "}
          {result.requiredWidth ?? "?"} px needed
        </dd>

        <dt className="text-white/55">expected</dt>
        <dd>
          {result.expectedWidth ? `${result.expectedWidth}w` : "—"}{" "}
          <span className={verdictTone}>({result.verdict}</span>
          <span className={verdictTone}>
            {result.overshootPct != null
              ? `, ${result.overshootPct > 0 ? "+" : ""}${result.overshootPct}%`
              : ""}
            )
          </span>
        </dd>

        <dt className="text-white/55">decode</dt>
        <dd
          className={
            result.decodeWithinTarget ? "text-emerald-300" : "text-rose-300"
          }
        >
          {result.decodeMs != null ? `${result.decodeMs} ms` : "n/a"} / target{" "}
          {result.decodeTargetMs} ms{" "}
          {result.decodeWithinTarget ? "within" : "over"}
        </dd>
      </dl>

      {result.notes.length > 0 && (
        <ul className="mt-2 space-y-0.5 border-t border-white/15 pt-2 text-[10px] text-amber-200">
          {result.notes.map((n) => (
            <li key={n}>⚠ {n}</li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SrcsetVerifier;
